import React, { useEffect, useState } from 'react';
import SearchIcon from '@mui/icons-material/Search';
import {
  Autocomplete,
  createFilterOptions,
  InputAdornment,
  Switch,
  TextField,
  TextFieldProps,
  Typography,
} from '@mui/material';
import { makeStyles } from '@mui/styles';

export type Option = {
  label: string | React.ReactNode;
  value: string;
  isDisabled?: boolean;
};

type Props = Omit<TextFieldProps, 'variant'> & {
  label?: string;
  errorMessage?: string;
  options: Option[];
  handleOnChange: Function;
  helperText?: string | null;
};

const useStyles = makeStyles(() => ({
  adornment: {
    paddingLeft: '10px',
    opacity: 0.5,
  },
  input: {
    width: '100%',
    maxWidth: '100%',
    paddingBottom: '10px',
  },
  switch: {
    width: '100%',
    maxWidth: '100%',
    marginTop: '10px',
    paddingTop: '10px',
  },
  textField: {
    '& .MuiInputBase-root': {
      padding: '0px',
    },
    '& .MuiInputBase-input': {
      height: '30px',
    },
  },
  helperText: {
    marginLeft: '5px',
    fontSize: '10px',
  },
}));

const AutocompleteFormSelectField = ({
  label,
  options,
  handleOnChange,
  helperText,
  ...other
}: Props) => {
  const classes = useStyles();
  const [value, setValue] = useState<Option | null>(null);
  const [inputValue, setInputValue] = useState<string>('');

  const [autoCompleteFilterOption, setAutoCompleteFilterOption] = useState<boolean>(true);

  const filterOptions = createFilterOptions({
    matchFrom: autoCompleteFilterOption ? 'start' : 'any',
    stringify: (option: Option) => option.label as string,
  });

  const handleOptionChange = (newValue: Option | null) => {
    setValue(newValue);
    handleOnChange(newValue?.value || null);
  };

  const handleSearchLoseFocus = () => {
    // make sure an invalid value isn't set after using the search filter
    setValue(options.find((option) => option.value === other.value) || null);
  };

  useEffect(() => {
    if (other.value) {
      setValue(options.find((option) => option.value === other.value) || null);
      setInputValue(
        options.find((option) => option.value === other.value)?.label?.toString() || '',
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (other.value === null) {
      setValue(null);
      setInputValue('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [other.value]);

  return (
    <div>
      <div className={classes.switch}>
        <Typography variant="caption">Filter at start?</Typography>
        <Switch
          checked={autoCompleteFilterOption}
          onChange={() => {
            setAutoCompleteFilterOption(!autoCompleteFilterOption);
          }}
          color="primary"
        />
      </div>
      <div className={classes.input}>
        {label && <Typography variant="caption">{label}</Typography>}
        <Autocomplete
          fullWidth
          autoHighlight
          options={options}
          filterOptions={filterOptions}
          disabled={other.disabled}
          value={value}
          onChange={(_: any, newValue: Option | null) => handleOptionChange(newValue)}
          inputValue={inputValue}
          onInputChange={(_, newInputValue) => {
            setInputValue(newInputValue);
          }}
          renderInput={(params) => (
            <TextField
              {...other}
              className={classes.textField}
              onBlur={handleSearchLoseFocus}
              onChange={(evt) => {
                handleOnChange(evt.currentTarget.value as string);
              }}
              {...params}
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <InputAdornment position="start" className={classes.adornment}>
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          )}
        />
        {helperText && <Typography className={classes.helperText}>{helperText}</Typography>}
      </div>
    </div>
  );
};

export default AutocompleteFormSelectField;
